import { Icon, Icons } from '@/components/icon';
import { OtpPrimary } from '@/components/otp/otp-primary';
import {
  PhoneInputField,
  formatPhoneNational,
  validateAndFormatPhone,
} from '@/components/phone-input/phone-input-field';
import { ClickToCallLineSelect } from '@/components/settings/click-to-call-line-select';
import { SettingsSectionHeader } from '@/components/settings/section-header';
import { Avatar, Badge, Button, Chip, Layout, TextInput } from '@/components/ui/design-system';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { useRoleLabel } from '@/hooks/use-role-label';
import { useTranslation } from '@/hooks/use-translation';
import { isDesktop } from '@/lib/platform';
import { cn } from '@/lib/utils';
import amplitudeService from '@/services/amplitude';
import { ANALYTICS_EVENTS, analytics } from '@/services/analytics';
import { $apiAllo } from '@/services/api-allo/client';
import { logger } from '@/services/logger/logger';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import type { CountryCode } from 'libphonenumber-js';
import { Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

const AVATAR_MAX_SIZE_BYTES = 10 * 1024 * 1024; // 10MB
const AVATAR_ALLOWED_TYPES = ['image/jpeg', 'image/png'];

export const Route = createFileRoute('/_app/settings/profile')({
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useTranslation('desktop_ui');
  const queryClient = useQueryClient();
  const { data: userData, isLoading } = $apiAllo.useQuery('get', '/v1/user', {});
  const { getRoleLabel } = useRoleLabel();

  const user = userData?.data as
    | (NonNullable<typeof userData>['data'] & { login_email?: string | null })
    | undefined;
  const fullName = user?.name ?? '';
  const personalPhone = user?.personal_phone;
  const loginEmail = user?.login_email ?? user?.email ?? '';
  const notificationsEmail = user?.email ?? '';
  const userRole = user?.team?.members?.find((member) => member.id === user?.id)?.team_member_role;

  const [localFullName, setLocalFullName] = useState(fullName);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [localNotificationsEmail, setLocalNotificationsEmail] = useState(notificationsEmail);
  const [notificationsEmailError, setNotificationsEmailError] = useState<string | null>(null);

  // Personal phone change flow
  const [isChangingPhone, setIsChangingPhone] = useState(false);
  const [phoneStep, setPhoneStep] = useState<'phone' | 'otp'>('phone');
  const [changePhone, setChangePhone] = useState('');
  const [changePhoneCountry, setChangePhoneCountry] = useState<CountryCode>('FR');
  const [changePhoneFormatted, setChangePhoneFormatted] = useState('');
  const [, setPhoneOtp] = useState('');
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [multiOutputEnabled, setMultiOutputEnabled] = useState<boolean>(false);
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const [avatarPreviewUrl, setAvatarPreviewUrl] = useState<string | null>(null);
  const avatarPreviewUrlRef = useRef<string | null>(null);
  avatarPreviewUrlRef.current = avatarPreviewUrl;

  const avatarUploadMutation = $apiAllo.useMutation('patch', '/v1/user/avatar', {
    onSuccess: (response) => {
      const userOptions = $apiAllo.queryOptions('get', '/v1/user', {});
      queryClient.setQueryData(userOptions.queryKey, (prev: typeof userData) => ({
        ...prev,
        data: prev?.data ? { ...prev.data, ...response.data } : response.data,
      }));
      queryClient.invalidateQueries($apiAllo.queryOptions('get', '/v1/business'));
      amplitudeService.logEvent('Profile Avatar Updated');
      toast.success(t('profile.imageUploadSuccess'));
    },
    onError: () => {
      const url = avatarPreviewUrlRef.current;
      if (url) {
        URL.revokeObjectURL(url);
        setAvatarPreviewUrl(null);
      }
      toast.error(t('profile.imageUploadError'));
    },
  });

  const updateUserMutation = $apiAllo.useMutation('patch', '/v1/user', {
    onSuccess: () => {
      queryClient.invalidateQueries($apiAllo.queryOptions('get', '/v1/user'));
      queryClient.invalidateQueries($apiAllo.queryOptions('get', '/v1/business'));

      amplitudeService.logEvent('Profile Settings Updated', {
        hasNameChange: localFullName.trim() !== fullName,
      });
      toast.success(t('profile.successTitle'), {
        description: t('profile.successDescription'),
      });
      setValidationError(null);
    },
    onError: (error) => {
      toast.error(t('profile.errorTitle'), {
        description: t('profile.errorDescription'),
      });
      console.error('Failed to update profile:', error);
    },
  });

  const updateNotificationsEmailMutation = $apiAllo.useMutation('patch', '/v1/user', {
    onSuccess: () => {
      queryClient.invalidateQueries($apiAllo.queryOptions('get', '/v1/user'));
      analytics.track(ANALYTICS_EVENTS.PROFILE.NOTIFICATIONS_EMAIL_UPDATED);
      toast.success(t('profile.notificationsEmailUpdated'));
      setNotificationsEmailError(null);
    },
    onError: (error) => {
      toast.error(t('profile.errorTitle'), {
        description: t('profile.errorDescription'),
      });
      logger.error('Failed to update notifications email', { error });
    },
  });

  const requestOtpMutation = $apiAllo.useMutation('post', '/v1/user/personal-phone/request-otp', {
    onSuccess: () => {
      setPhoneError(null);
      setPhoneStep('otp');
    },
    onError: () => {
      toast.error(t('profile.errorTitle'), {
        description: t('profile.errorDescription'),
      });
    },
  });

  const verifyPhoneMutation = $apiAllo.useMutation('post', '/v1/user/personal-phone/verify', {
    onSuccess: () => {
      queryClient.invalidateQueries($apiAllo.queryOptions('get', '/v1/user'));
      toast.success(t('profile.successTitle'), {
        description: t('profile.otpSuccess'),
      });
      setIsChangingPhone(false);
      setPhoneStep('phone');
      setChangePhone('');
      setPhoneOtp('');
    },
    onError: () => {
      setPhoneError(t('profile.otpInvalid'));
      setPhoneOtp('');
    },
  });

  useEffect(() => {
    amplitudeService.logEvent('Profile Settings Displayed');
    try {
      const value = localStorage.getItem('ringtoneMultiOutput');
      setMultiOutputEnabled(value === 'true' || value === '1');
    } catch {}
  }, []);

  useEffect(() => {
    setLocalFullName(fullName);
  }, [fullName]);

  useEffect(() => {
    setLocalNotificationsEmail(notificationsEmail);
  }, [notificationsEmail]);

  // Preload new avatar URL before switching from preview to avoid initials flash
  useEffect(() => {
    const newImageUrl = user?.image_url;
    if (!avatarPreviewUrl || !newImageUrl) {
      return;
    }

    const img = new Image();
    const handleLoad = () => {
      URL.revokeObjectURL(avatarPreviewUrl);
      setAvatarPreviewUrl(null);
    };
    img.onload = handleLoad;
    img.onerror = () => setAvatarPreviewUrl(null);
    img.src = newImageUrl;
    return () => {
      img.onload = null;
      img.onerror = null;
      img.src = '';
    };
  }, [user?.image_url, avatarPreviewUrl]);

  const handleSave = () => {
    const trimmedName = localFullName.trim();

    if (!trimmedName) {
      setValidationError(t('profile.nameRequired'));
      return;
    }

    setValidationError(null);
    updateUserMutation.mutate({ body: { name: trimmedName } });
  };

  const handleRequestPhoneOtp = () => {
    if (!changePhone.replace(/\s/g, '')) {
      setPhoneError(t('profile.phoneRequired'));
      return;
    }

    const formatted = validateAndFormatPhone(changePhone, changePhoneCountry);
    if (!formatted) {
      setPhoneError(t('profile.phoneInvalid'));
      return;
    }

    setPhoneError(null);
    setChangePhoneFormatted(formatted);
    requestOtpMutation.mutate({ body: { number: formatted } });
  };

  const handleVerifyPhone = (code: string) => {
    setPhoneError(null);
    verifyPhoneMutation.mutate({ body: { number: changePhoneFormatted, otp: code } });
  };

  const hasChanges = localFullName.trim() !== fullName;

  const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const trimmedNotificationsEmail = localNotificationsEmail.trim();
  const hasNotificationsEmailChange =
    trimmedNotificationsEmail !== notificationsEmail && trimmedNotificationsEmail.length > 0;

  const handleSaveNotificationsEmail = () => {
    if (!trimmedNotificationsEmail) {
      setNotificationsEmailError(t('profile.emailRequired'));
      return;
    }

    if (!isValidEmail(trimmedNotificationsEmail)) {
      setNotificationsEmailError(t('profile.emailInvalid'));
      return;
    }

    setNotificationsEmailError(null);
    updateNotificationsEmailMutation.mutate({ body: { email: trimmedNotificationsEmail } });
  };

  const handleAudioToggle = (checked: boolean) => {
    amplitudeService.logEvent('Audio Settings Tapped', {
      State: checked ? 'Enabled' : 'Disabled',
    });
    setMultiOutputEnabled(checked);
    try {
      localStorage.setItem('ringtoneMultiOutput', checked ? 'true' : 'false');
    } catch {}
  };

  const handleAvatarChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    if (!AVATAR_ALLOWED_TYPES.includes(file.type)) {
      toast.error(t('profile.imageUploadErrorInvalidType'));
      return;
    }

    if (file.size > AVATAR_MAX_SIZE_BYTES) {
      toast.error(t('profile.imageUploadErrorFileTooLarge'));
      return;
    }

    if (avatarPreviewUrl) {
      URL.revokeObjectURL(avatarPreviewUrl);
    }
    setAvatarPreviewUrl(URL.createObjectURL(file));

    const formData = new FormData();
    formData.append('file', file);
    avatarUploadMutation.mutate({
      // @ts-expect-error openapi-fetch accepts FormData for multipart; types expect schema shape
      body: formData,
    });

    event.target.value = '';
  };

  if (isLoading) {
    return (
      <Layout isScrollable contentClassName="max-w-[960px] px-20 !pt-0 pb-30">
        {/* Profile information skeleton */}
        <section className="flex flex-col gap-6">
          <SettingsSectionHeader
            title={t('profile.profileInformation')}
            description={t('profile.profileInformationDescription')}
          />
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-6">
              <div className="flex max-w-[560px] flex-1 flex-col gap-1">
                <Skeleton className="h-4 w-24" />
              </div>
              <div className="flex flex-1 items-center justify-end">
                <Skeleton className="h-8 w-full max-w-[320px]" />
              </div>
            </div>
          ))}
        </section>
      </Layout>
    );
  }

  return (
    <Layout isScrollable contentClassName="max-w-[960px] px-20 !pt-0 pb-30">
      {/* Section: Profile information */}
      <section className="flex flex-col gap-6">
        <SettingsSectionHeader
          title={t('profile.profileInformation')}
          description={t('profile.profileInformationDescription')}
        />

        {/* Profile picture */}
        <div className="flex items-center gap-6">
          <div className="flex max-w-[560px] flex-1 flex-col">
            <span className="text-body-14 text-primary-100">{t('profile.profilePicture')}</span>
          </div>
          <div className="flex flex-1 items-center justify-end">
            <button
              type="button"
              onClick={() => avatarInputRef.current?.click()}
              disabled={avatarUploadMutation.isPending}
              aria-label={t('profile.changePhoto')}
              className={cn(
                'group relative inline-flex shrink-0 cursor-pointer items-center justify-center rounded-xl outline-none',
                'focus-visible:ring-2 focus-visible:ring-primary-100 focus-visible:ring-offset-2',
                'disabled:cursor-not-allowed disabled:opacity-70',
              )}
            >
              <Chip size="lg">
                {(() => {
                  const imageUrl = avatarPreviewUrl ?? user?.image_url;
                  if (imageUrl) {
                    return <Avatar type="image" src={imageUrl} name={fullName || undefined} />;
                  }
                  return fullName ? (
                    <Avatar type="initial" name={fullName} />
                  ) : (
                    <Avatar type="unknown" />
                  );
                })()}
              </Chip>
              <span
                className={cn(
                  'absolute inset-0 flex size-full items-center justify-center rounded-xl bg-primary-100/60 transition-opacity',
                  avatarUploadMutation.isPending
                    ? 'opacity-100'
                    : 'opacity-0 group-hover:opacity-100',
                )}
              >
                {avatarUploadMutation.isPending ? (
                  <Loader2 className="size-4 shrink-0 animate-spin text-white" />
                ) : (
                  <Icon name={Icons.CameraOutline} className="shrink-0 text-s text-white" />
                )}
              </span>
            </button>
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/jpeg,image/png"
              className="sr-only"
              onChange={handleAvatarChange}
            />
          </div>
        </div>

        <div className="h-px bg-primary-5" />

        {/* Full name */}
        <div className="flex items-center gap-6">
          <div className="flex max-w-[560px] flex-1 flex-col">
            <Label htmlFor="full-name" className="text-body-14 text-primary-100">
              {t('profile.fullName')}
            </Label>
          </div>
          <div className="flex flex-1 items-center gap-2 justify-end">
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <TextInput
                id="full-name"
                value={localFullName}
                onChange={(e) => {
                  setLocalFullName(e.target.value);
                  if (validationError) {
                    setValidationError(null);
                  }
                }}
                fit="parent"
                variant="highlight"
                isInvalid={!!validationError}
                placeholder={t('profile.fullName')}
                className="rounded-lg"
              />
              {validationError && (
                <span className="text-body-12 text-status-danger">{validationError}</span>
              )}
            </div>
          </div>
        </div>

        <div className="h-px bg-primary-5" />

        {/* Login email (read-only) */}
        <div className="flex items-center gap-6">
          <div className="flex max-w-[560px] flex-1 flex-col">
            <span className="text-body-14 text-primary-100">{t('profile.email')}</span>
            <span className="text-pretty text-body-13 text-primary-50">
              {t('profile.emailReadOnlyHint')}
            </span>
          </div>
          <div className="flex flex-1 items-center justify-end">
            <div className="flex h-8 min-w-0 flex-1 items-center gap-2 rounded-lg border border-primary-10 bg-primary-3 px-3">
              <span className="min-w-0 flex-1 truncate text-body-13 text-primary-100">
                {loginEmail}
              </span>
              <Icon name={Icons.PadlockFill} className="shrink-0 text-s text-primary-40" />
            </div>
          </div>
        </div>

        {userRole && (
          <>
            <div className="h-px bg-primary-5" />

            {/* Role — selector-style read-only display (matches Voicemail / Voice selectors) */}
            <div className="flex items-center gap-6">
              <div className="flex max-w-[560px] flex-1 flex-col">
                <span className="text-body-14 text-primary-100">{t('profile.role')}</span>
              </div>
              <div className="flex flex-1 items-center justify-end">
                <div className="flex h-8 min-w-0 flex-1 items-center gap-2 rounded-lg border border-primary-10 bg-primary-3 px-3">
                  <span className="min-w-0 flex-1 truncate text-body-13 text-primary-100">
                    {getRoleLabel(userRole as 'ADMIN' | 'MANAGER' | 'MEMBER')}
                  </span>
                  <Icon name={Icons.ChevronDown} className="shrink-0 text-m text-primary-30" />
                </div>
              </div>
            </div>
          </>
        )}

        <div className="h-px bg-primary-5" />

        {/* Personal phone — verified badge sits next to the title, not in the input */}
        <div className="flex items-start gap-6">
          <div className="flex max-w-[560px] flex-1 flex-col">
            <div className="flex items-center gap-2">
              <span className="text-body-14 text-primary-100">{t('profile.personalPhone')}</span>
              {!isChangingPhone && personalPhone && (
                <Badge variant="green" size="sm">
                  {t('profile.phoneVerified')}
                </Badge>
              )}
            </div>
            <span className="text-pretty text-body-13 text-primary-50">
              {t('profile.personalPhoneHint')}
            </span>
          </div>
          <div className="flex flex-1 items-center gap-2 justify-end">
            {isChangingPhone ? (
              phoneStep === 'otp' ? (
                <div className="flex min-w-0 flex-1 flex-col items-end gap-2">
                  <p className="text-body-13 text-primary-60 text-right">
                    {t('profile.otpDescription')}{' '}
                    <span className="font-semibold text-primary-100">{changePhoneFormatted}</span>
                  </p>
                  <OtpPrimary
                    length={6}
                    autoFocus
                    onChange={setPhoneOtp}
                    onComplete={handleVerifyPhone}
                  />
                  {phoneError && (
                    <span className="text-body-12 text-status-danger">{phoneError}</span>
                  )}
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setPhoneError(null);
                        setPhoneOtp('');
                        requestOtpMutation.mutate({ body: { number: changePhoneFormatted } });
                      }}
                      disabled={requestOtpMutation.isPending}
                      className="text-body-13 text-primary-100 hover:underline disabled:opacity-50"
                    >
                      {t('profile.otpResend')}
                    </button>
                    <span className="text-body-13 text-primary-40">|</span>
                    <button
                      type="button"
                      onClick={() => {
                        setPhoneStep('phone');
                        setPhoneError(null);
                        setPhoneOtp('');
                      }}
                      className="text-body-13 text-primary-100 hover:underline"
                    >
                      {t('profile.changePhone')}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex min-w-0 flex-1 flex-col items-end gap-2">
                  <PhoneInputField
                    value={changePhone}
                    onChange={(v) => {
                      setChangePhone(v);
                      if (phoneError) {
                        setPhoneError(null);
                      }
                    }}
                    countryCode={changePhoneCountry}
                    onCountryChange={setChangePhoneCountry}
                    isInvalid={!!phoneError}
                    variant="highlight"
                    className="rounded-lg"
                  />
                  {phoneError && (
                    <span className="text-body-12 text-status-danger">{phoneError}</span>
                  )}
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setIsChangingPhone(false);
                        setPhoneError(null);
                      }}
                    >
                      {t('profile.cancel')}
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleRequestPhoneOtp}
                      disabled={requestOtpMutation.isPending}
                    >
                      {t('profile.phoneModalSave')}
                    </Button>
                  </div>
                </div>
              )
            ) : personalPhone ? (
              <>
                <div className="flex h-8 min-w-0 flex-1 items-center rounded-lg border border-primary-10 bg-primary-3 px-3">
                  <span className="min-w-0 flex-1 truncate text-body-13 text-primary-100">
                    {formatPhoneNational(personalPhone)}
                  </span>
                </div>
                <Button variant="outline" size="md" onClick={() => setIsChangingPhone(true)}>
                  {t('profile.changePhone')}
                </Button>
              </>
            ) : (
              <Button variant="outline" size="sm" onClick={() => setIsChangingPhone(true)}>
                {t('profile.changePhone')}
              </Button>
            )}
          </div>
        </div>

        {/* Save action — only when there's a pending change on a non-phone field */}
        {hasChanges && (
          <div className="flex justify-end">
            <Button
              size="sm"
              onClick={handleSave}
              isLoading={updateUserMutation.isPending}
              disabled={!hasChanges}
            >
              {t('profile.saveChanges')}
            </Button>
          </div>
        )}

        <div className="h-px bg-primary-5" />
      </section>

      {/* Section: Notifications */}
      <section className="flex flex-col gap-6">
        <SettingsSectionHeader title={t('profile.notificationsTitle')} />

        <div className="flex items-start gap-6">
          <div className="flex max-w-[560px] flex-1 flex-col">
            <Label htmlFor="notifications-email" className="text-body-14 text-primary-100">
              {t('profile.notificationsEmail')}
            </Label>
            <span className="text-pretty text-body-13 text-primary-50">
              {t('profile.notificationsEmailHint')}
            </span>
          </div>
          <div className="flex flex-1 items-center gap-2 justify-end">
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <TextInput
                id="notifications-email"
                value={localNotificationsEmail}
                onChange={(e) => {
                  setLocalNotificationsEmail(e.target.value);
                  if (notificationsEmailError) {
                    setNotificationsEmailError(null);
                  }
                }}
                fit="parent"
                variant="highlight"
                isInvalid={!!notificationsEmailError}
                placeholder={t('profile.notificationsEmail')}
                className="rounded-lg"
              />
              {notificationsEmailError && (
                <span className="text-body-12 text-status-danger">{notificationsEmailError}</span>
              )}
            </div>
          </div>
        </div>

        {hasNotificationsEmailChange && (
          <div className="flex justify-end">
            <Button
              size="sm"
              onClick={handleSaveNotificationsEmail}
              isLoading={updateNotificationsEmailMutation.isPending}
              disabled={!hasNotificationsEmailChange}
            >
              {t('profile.saveChanges')}
            </Button>
          </div>
        )}

        <div className="h-px bg-primary-5" />
      </section>

      {/* Section: Audio settings */}
      <section className="flex flex-col gap-6">
        <SettingsSectionHeader title={t('audioSettings.title')} />

        <label
          htmlFor="multi-output"
          className="flex cursor-pointer items-center justify-between gap-6"
        >
          <div className="flex max-w-[560px] flex-1 flex-col">
            <span className="text-body-14 text-primary-100">
              {t('audioSettings.multiOutput.label')}
            </span>
            <span className="text-pretty text-body-13 text-primary-50">
              {t('audioSettings.multiOutput.description')}
            </span>
          </div>
          <Switch
            id="multi-output"
            size="sm"
            checked={multiOutputEnabled}
            onCheckedChange={handleAudioToggle}
          />
        </label>

        <div className="h-px bg-primary-5" />
      </section>

      {/* Click-to-call relies on the OS tel:/callto: handler only the desktop app registers. */}
      {isDesktop() && (
        <section className="flex flex-col gap-6">
          <SettingsSectionHeader title={t('clickToCall.title')} />

          <div className="flex items-center gap-6">
            <div className="flex max-w-[560px] flex-1 flex-col">
              <span className="text-body-14 text-primary-100">{t('clickToCall.line.label')}</span>
              <span className="text-pretty text-body-13 text-primary-50">
                {t('clickToCall.line.description')}
              </span>
            </div>
            <div className="flex flex-1 items-center justify-end">
              <ClickToCallLineSelect />
            </div>
          </div>

          <div className="h-px bg-primary-5" />
        </section>
      )}
    </Layout>
  );
}
